// Dictionary, 810 part — the 810-specific segments with no shared
// equivalent: BIG (Beginning Segment for Invoice), IT1 (Baseline Item
// Data, the 810's line-item segment), TDS (Total Monetary Value Summary),
// CAD (Carrier Detail) and TXI (Tax Information). Generic X12 810 (005010)
// definitions only; not tied to any trading partner's implementation guide.
//
// Everything else the 810 header, its Name (N1) loop, its IT1 loop and its
// summary use — CUR, REF, PER, PWK, FOB, ITD, DTM, MSG, N1-N9, SAC, CTT (in
// dictionary/header-segments.ts and dictionary/item-loop-segments.ts) — is a
// segment X12 defines once and reuses across transaction sets, so it's
// already defined elsewhere and merged in by index.ts. This file does not
// redefine any of them.

import {LoopId, type ElementDefinition, type SegmentDefinition} from './types';
import {TRANSACTION_SET_PURPOSE_CODES} from './transaction-set-purpose-codes';
import {TRANSACTION_TYPE_CODES} from './transaction-type-codes';
import {TAX_TYPE_CODES} from './tax-type-codes';
import {UNIT_OR_BASIS_FOR_MEASUREMENT_CODES} from './unit-or-basis-for-measurement-codes';
import {BASIS_OF_UNIT_PRICE_CODES} from './basis-of-unit-price-codes';
import {PRODUCT_SERVICE_ID_QUALIFIER_CODES} from './product-service-id-qualifiers';
import {TRANSPORTATION_METHOD_CODES} from './transportation-method-codes';
import {IDENTIFICATION_CODE_QUALIFIER_CODES} from './identification-code-qualifiers';

function el(
  position: number,
  label: string,
  description: string,
  extra: Partial<ElementDefinition> = {}
): ElementDefinition {
  return {position, label, description, ...extra};
}

function productServiceIdQualifier(position: number): ElementDefinition {
  return el(
    position,
    'Product/Service ID Qualifier',
    'A subsequent item identifier scheme, when the item carries more than two ids.',
    {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
  );
}

export const SEGMENTS_810: Record<string, SegmentDefinition> = {
  BIG: {
    id: 'BIG',
    name: 'Beginning Segment for Invoice',
    purpose:
      'Identifies the invoice number and date, the related purchase order, and what kind of invoice this is. Required at the start of every 810.',
    loop: LoopId.Header,
    required: true,
    elements: [
      el(1, 'Date', 'The invoice date.', {
        dataType: 'date',
        format: 'CCYYMMDD',
        required: true,
      }),
      el(2, 'Invoice Number', "The seller's invoice number.", {
        required: true,
      }),
      el(3, 'Date', 'The purchase order date.', {
        dataType: 'date',
        format: 'CCYYMMDD',
      }),
      el(4, 'Purchase Order Number', 'The related purchase order number.'),
      el(5, 'Release Number', 'The release number against a blanket order.'),
      el(
        6,
        'Change Order Sequence Number',
        'Which revision of the purchase order this invoice is against.'
      ),
      el(
        7,
        'Transaction Type Code',
        'What kind of invoice this is — a debit/credit invoice or memo.',
        {dataType: 'code', codes: TRANSACTION_TYPE_CODES}
      ),
      el(
        8,
        'Transaction Set Purpose Code',
        'Whether this is an original invoice, a cancellation, a replacement, etc.',
        {dataType: 'code', codes: TRANSACTION_SET_PURPOSE_CODES}
      ),
    ],
  },

  IT1: {
    id: 'IT1',
    name: 'Baseline Item Data',
    purpose:
      'Opens one invoice line: the line number, quantity invoiced, unit price and the item being billed. Every item loop starts with an IT1.',
    loop: LoopId.Item,
    required: true,
    elements: [
      el(
        1,
        'Assigned Identification',
        'The line number, assigned by the seller.'
      ),
      el(2, 'Quantity Invoiced', 'How many units are being invoiced.', {
        dataType: 'numeric',
        required: true,
      }),
      el(
        3,
        'Unit or Basis for Measurement Code',
        'The unit the quantity is expressed in.',
        {
          dataType: 'code',
          required: true,
          codes: UNIT_OR_BASIS_FOR_MEASUREMENT_CODES,
        }
      ),
      el(4, 'Unit Price', 'The price per unit.', {
        dataType: 'numeric',
        required: true,
      }),
      el(5, 'Basis of Unit Price Code', 'What the unit price is quoted per.', {
        dataType: 'code',
        codes: BASIS_OF_UNIT_PRICE_CODES,
      }),
      el(
        6,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows.',
        {
          dataType: 'code',
          codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES,
        }
      ),
      el(
        7,
        'Product/Service ID',
        'The item identifier, in the scheme named by element 6.'
      ),
      el(
        8,
        'Product/Service ID Qualifier',
        'A second item identifier scheme, when the item carries more than one id.',
        {
          dataType: 'code',
          codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES,
        }
      ),
      el(9, 'Product/Service ID', 'The second item identifier.'),
      productServiceIdQualifier(10),
      productServiceIdQualifier(12),
      productServiceIdQualifier(14),
      productServiceIdQualifier(16),
      productServiceIdQualifier(18),
      productServiceIdQualifier(20),
      productServiceIdQualifier(22),
      productServiceIdQualifier(24),
    ],
  },

  TDS: {
    id: 'TDS',
    name: 'Total Monetary Value Summary',
    purpose:
      'States the total invoice amount and the terms-discount amounts it is subject to.',
    loop: LoopId.Summary,
    elements: [
      el(1, 'Amount', 'The total invoice amount.', {
        dataType: 'numeric',
        required: true,
      }),
      el(2, 'Amount', 'The amount subject to the terms discount.', {
        dataType: 'numeric',
      }),
      el(3, 'Amount', 'The amount of the terms discount.', {
        dataType: 'numeric',
      }),
    ],
  },

  CAD: {
    id: 'CAD',
    name: 'Carrier Detail',
    purpose: 'Identifies how the shipment moved and who carried it.',
    loop: LoopId.Header,
    elements: [
      el(
        1,
        'Transportation Method/Type Code',
        'How the shipment was transported.',
        {dataType: 'code', codes: TRANSPORTATION_METHOD_CODES}
      ),
      el(2, 'Routing', 'Free-form routing information.'),
      el(
        3,
        'Identification Code Qualifier',
        'What kind of carrier identifier follows.',
        {dataType: 'code', codes: IDENTIFICATION_CODE_QUALIFIER_CODES}
      ),
      el(4, 'Carrier Identification', 'The carrier identifier.'),
      el(
        5,
        'Identification Code Qualifier',
        'What kind of second identifier follows.',
        {dataType: 'code', codes: IDENTIFICATION_CODE_QUALIFIER_CODES}
      ),
      el(
        6,
        'Reference Identification',
        'A related reference number, e.g. a bill of lading number.'
      ),
    ],
  },

  TXI: {
    id: 'TXI',
    name: 'Tax Information',
    purpose:
      'States a tax amount and what kind of tax it is, at either the line or the summary level.',
    loop: LoopId.Item,
    elements: [
      el(1, 'Tax Type Code', 'What kind of tax this is.', {
        dataType: 'code',
        required: true,
        codes: TAX_TYPE_CODES,
      }),
      el(2, 'Amount', 'The tax amount.', {dataType: 'numeric'}),
    ],
  },
};
